import get from 'lodash/get';
import { useObserver } from 'mobx-react-lite';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useQuery } from 'hooks/use-query';
import { useDispensaryCategory } from './use-dispensary-category';
import useCart from './use-cart';
import useUI from './use-ui';
import { getQuery, getVariables, getCategoriesSubcategoriesAsOptions } from './use-menu-filter-options';
import type { MenuFilterOptions } from '../components/menu-sidebar/types';

type UseGlobalCategoryFilterOptionsProps = {
  skip?: boolean;
};

type UseGlobalCategoryFilterOptionsReturn = {
  globalCategoriesSubcategories: MenuFilterOptions['categoriesSubcategories'];
  loading: boolean;
};

/**
 * Thin wrapper around the same `menuFiltersV2` query used by useMenuFilterOptions, but
 * unscoped from the active category (skipCurrentFilters: true) so it returns every
 * top-level category present in the dispensary's catalog. Used to populate the global
 * category list in the sidebar on category pages (see PF-9862) without touching the
 * scoped call that drives subcategory/weight/etc. filters.
 */
export default function useGlobalCategoryFilterOptions({
  skip = false,
}: UseGlobalCategoryFilterOptionsProps = {}): UseGlobalCategoryFilterOptionsReturn {
  const cart = useCart();
  const category = useDispensaryCategory();
  const { dispensary } = useDispensary();
  const UI = useUI();
  const isKioskMenu = useObserver(() => UI.isKiosk);

  const variables = getVariables({
    dispensary,
    category,
    skipCurrentFilters: true,
    search: undefined,
    pricingType: cart?.menuType,
    isOnSpecial: false,
    specialId: undefined,
    specialType: undefined,
    isKioskMenu,
    onSearchPage: false,
    collectionId: undefined,
  });

  const { data, loading } = useQuery(getQuery(), {
    variables,
    skip: skip || !dispensary,
  });

  const categoriesSubcategoriesFromQuery = get(data, `menuFiltersV2.categoriesSubcategories`, []);

  const globalCategoriesSubcategories = getCategoriesSubcategoriesAsOptions(
    categoriesSubcategoriesFromQuery,
    dispensary?.location?.state
  );

  return {
    globalCategoriesSubcategories,
    loading,
  };
}
